/* eslint-disable max-len */
import { Box, Button, Typography } from "@mui/material";
import React, { useEffect, useMemo, useRef, useState } from "react";

import { CarePortalContainer } from "./CarePortal.style";
import CategoriesPanel from "./CategoriesPanel";
import { Loading } from "../../../components";
import { MIXPANEL_EVENTS } from "../../../config/mixPanelEvents";
import { MODALS } from "../../../config/params";
import { PAGES_PATH } from "../../../navigation/params";
import PetProfileCard from "./PetProfileCard";
import RightPanelContainer from "../../../pages/Dashboard/components/RightPanelContainer";
import { useModal } from "../../../context/ModalContext";
import { usePetCareStore } from "../../../hooks/redux-hooks/pet-care-data/pet-care-data-store";
import { usePetGeneralCare } from "../../../services/Standard-of-care/standard-of-care.service";
import { usePetList } from "../../../services/pet-list/pet-list.service";
import { usePetStore } from "../../../hooks/redux-hooks/pet-store/pet-store.service";
import { useRedirectWithToast } from "../../../hooks/useRedirectWithToast";
import { useRetirePetStore } from "../../../hooks/redux-hooks/pet-retire-store/pet-retire-store";
import { useSearchParams } from "react-router-dom";
import { userActivityLogger } from "../../../utils/mixpanel";

const PetCareContainer = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { openModal, modalView } = useModal();
  const petId = searchParams.get("id");
  const category = searchParams.get("category");
  const [currentPetId, setCurrentPetId] = useState<string | null>();

  // network-only: the Apollo cache is never cleared on logout, so a cached
  // first render would validate ?id against the previous account's pets and
  // false-redirect. The watcher still picks up refetchQueries (pet added) and
  // cache eviction (pet retired).
  const { petsActive: petListData, loading, refetch: refetchPetList } = usePetList({
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
  });

  const { getGeneralCare, petCare, isLoading } = usePetGeneralCare();
  const { petCareData, setPetCareData, selectedPet, setSelectedPet, clearSelectedPet } = usePetCareStore();
  const [selectedCategory, setSelectedCategory] = useState<string>(category ? category : "Care");
  const { isPetDeleted, setDeletedStatus } = useRetirePetStore();
  const { petInfoMap } = usePetStore();
  const [hospitalDetailsContentful, setHospitalDetailsContentful] = useState<any>(null);

  // Redirect utility for validation errors
  const { redirectWithToast } = useRedirectWithToast();

  const handleSelectCategory = (type: string) => {
    setSelectedCategory(type);
  };

  useEffect(() => {
    return () => {
      setPetCareData(undefined);
      clearSelectedPet();
    };
  }, []);

  useEffect(() => {
    if (modalView === MODALS.PET_BIRTHDAY_FORM_MODAL) {
      setSelectedCategory("Care");
    }
  }, [modalView]);

  useEffect(() => {
    if (isPetDeleted) {
      const handleDeletion = async () => {
        const freshPets = await refetchPetList()
          .then((result) => result.data?.getPets?.pets)
          .catch(() => undefined);
        if (freshPets && freshPets.length > 0) {
          setCurrentPetId(freshPets[0].id);
          getGeneralCare(freshPets[0].id);
        } else {
          setCurrentPetId(null);
        }
        setDeletedStatus(false);
      };
      handleDeletion();
    }
  }, [isPetDeleted]);

  // Validate petId when pet list is loaded
  useEffect(() => {
    // Skip during deletion -- the isPetDeleted useEffect handles that flow
    if (isPetDeleted) return;

    if (petListData && petListData.length) {
      if (petId) {
        const petExists = petListData.some((pet) => pet.id === petId);

        if (!petExists) {
          redirectWithToast("Pet not found. Please select from your pets", PAGES_PATH.DASHBOARD);
          return;
        }

        if (currentPetId !== petId) {
          setCurrentPetId(petId);
          getGeneralCare(petId);
        }
      } else if (!currentPetId) {
        setCurrentPetId(petListData[0]?.id);
        getGeneralCare(petListData[0]?.id);
      }
    }
  }, [petListData, petId, isPetDeleted]);

  useEffect(() => {
    if (currentPetId) {
      setSearchParams({ id: currentPetId }, { replace: true });
    }
  }, [currentPetId]);

  useEffect(() => {
    if (petCare) {
      setPetCareData(petCare);
    }
  }, [petCare]);

  const careRetryCount = useRef(0);
  const [careRetryPending, setCareRetryPending] = useState(false);

  useEffect(() => {
    careRetryCount.current = 0;
  }, [currentPetId]);

  // The backend SOC sync rebuilds Soc docs non-atomically (deleteMany ->
  // insertMany) on every dashboard query; a care fetch landing in that window
  // reports socStatus "" (or a null payload on a swallowed backend error).
  // Retry instead of caching the degraded read.
  useEffect(() => {
    if (!currentPetId) return;
    const currentPet = petCare?.pets.find((pet) => pet.id === currentPetId);
    const degraded = petCare === null || currentPet?.socStatus === "";

    if (degraded && careRetryCount.current < 2) {
      careRetryCount.current += 1;
      setCareRetryPending(true);
      const timer = setTimeout(() => getGeneralCare(currentPetId), 3000);

      return () => clearTimeout(timer);
    }
    setCareRetryPending(false);
  }, [petCare, currentPetId]);

  const patchedPetCareData = useMemo(() => {
    if (!petCareData) return undefined;
    const mapKeys = Object.keys(petInfoMap);
    if (mapKeys.length === 0) return petCareData;

    const patchedPets = petCareData.pets.map((p) => {
      const edit = petInfoMap[p.id];
      if (!edit) return p;
      return {
        ...p,
        name: edit.name ?? p.name,
        image: edit.image ?? p.image,
        species: edit.species ?? p.species,
        speciesName: edit.speciesName ?? p.speciesName,
        breed: edit.breed ?? p.breed,
        breedName: edit.breedName ?? p.breedName,
        birthdate: edit.birthdate ?? p.birthdate,
        gender: edit.gender ?? p.gender,
        age: edit.age ?? p.age,
        isDesexed: edit.isDesexed ?? p.isDesexed,
        idColor: edit.idColor ?? p.idColor,
        colorName: edit.colorName ?? p.colorName,
        medicalRecord: edit.medicalRecord ?? p.medicalRecord,
        microchip: edit.microchip ?? p.microchip,
      };
    });

    return { ...petCareData, pets: patchedPets };
  }, [petCareData, petInfoMap]);

  useEffect(() => {
    if (patchedPetCareData && currentPetId) {
      const pet = patchedPetCareData.pets.find((pet) => pet.id === currentPetId);

      if (pet) {
        setSelectedPet(pet);
      }
    }
  }, [patchedPetCareData, currentPetId]);

  const handlePetChange = (petId: string, category?: string) => {
    setCurrentPetId(petId);
    setSelectedCategory("Care");
    const petData = petListData?.find((data) => data.id == petId);

    userActivityLogger(MIXPANEL_EVENTS.USER_CLICK_PET_CARD, { pet_name: petData?.name, referred_from: "care_section" });
    category ? setSelectedCategory(category) : setSelectedCategory("Care");
  };

  const handleGoToAddAPet = () => {
    userActivityLogger(MIXPANEL_EVENTS.ADD_PET, { referred_from: "SOC" });

    openModal(MODALS.ADD_A_PET);
  };

  const setHospitalDetailsContent = (hospitalDetails: any) => {
    setHospitalDetailsContentful(hospitalDetails);
  };

  if (loading || (isLoading && !careRetryPending)) {
    return (
      <Box className="h-3/4 w-full flex justify-center items-center">
        <Loading />
      </Box>
    );
  }

  return (
    <>
      {(petListData && petListData.length > 0) || currentPetId ? (
        <CarePortalContainer>
          {careRetryPending && !patchedPetCareData ? (
            <Box className="h-3/4 w-full flex justify-center items-center">
              <Loading />
            </Box>
          ) : patchedPetCareData && patchedPetCareData.pets.length > 0 && selectedPet ? (
            <>
              <PetProfileCard
                petData={selectedPet}
                petList={patchedPetCareData.pets}
                handlePetChange={handlePetChange}
                careData={selectedPet.items}
              />
              <CategoriesPanel
                careData={selectedPet.items}
                petData={selectedPet}
                selectedCategory={selectedCategory}
                onSelectCategory={handleSelectCategory}
                petList={patchedPetCareData.pets}
                handlePetChange={handlePetChange}
                hospitalDetailsContentful={hospitalDetailsContentful}
              />
              <div className="hidden h-full max-w-[23rem] ms-10 3xl:flex flex-col gap-10">
                <RightPanelContainer setHospitalDetailsContent={setHospitalDetailsContent} />
              </div>
            </>
          ) : (
            <Box className="text-center py-4 px-3 m-auto max-w-[360px]">
              <img src="/assets/icons/no_pets.png" alt="No pets" />
              <Typography variant="h4" sx={{ my: 2 }}>
                Start by adding your first pet
              </Typography>
              <Button
                id="addYourFirstPet"
                color="primary"
                size="small"
                onClick={handleGoToAddAPet}
                className="!normal-case !text-xs"
                fullWidth
              >
                Add Your First Pet
              </Button>
            </Box>
          )}
        </CarePortalContainer>
      ) : (
        <Box className="text-center py-4 px-3 m-auto max-w-[360px]">
          <img src="/assets/icons/no_pets.png" alt="No pets" />
          <Typography variant="h4" sx={{ my: 2 }}>
            Start by adding your first pet
          </Typography>
          <Button
            id="addYourFirstPet"
            color="primary"
            size="small"
            onClick={handleGoToAddAPet}
            className="!normal-case !text-xs"
            fullWidth
          >
            Add Your First Pet
          </Button>
        </Box>
      )}
    </>
  );
};

export default PetCareContainer;
